import classNames from 'classnames';
import { cloneDeep, isEqual } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Form as RBForm } from 'react-bootstrap';
import ObjectID from 'bson-objectid';
import { Button, FormInput, Form, FormSection } from '@praktio/common-ui';

import Tree from '../../common/Tree/Tree';
import { RootState } from '../../../ducks/store';
import {
  getSettings,
  getPlatformSettings,
  updateSettings,
  reloadSSOConfigs,
} from '../../../ducks/settings';
import DragDropItem from '../../common/DragDropItem';
import AddUpdateNoticeModal from '../AddUpdateNoticeModal';
import { Timezone } from '../../../utils/constants';
import {
  buildTeamHierarchy,
  getISODateString,
  getMaintenanceMessage,
  useQuery,
} from '../../../utils/functions';
import { getTeams } from '../../../ducks/teams';

import './SystemSettings.scss';

interface ISystemSettingsProps {
  notifyHasChanges: (changes: boolean) => void;
  isPlatform?: boolean;
}

const TenantSpecificFields = ({
  settings,
  handleFormEdit,
}: {
  settings: ISettings;
  handleFormEdit: (value: string | number, prop?: string) => void;
}) => {
  const onExpirationSpanChange = (e: any) => {
    handleFormEdit(e.target.value, 'passwordExpirationSpan');
  };

  return (
    <FormInput
      onChange={onExpirationSpanChange}
      onBlur={() => {
        let newValue = settings?.passwordExpirationSpan || 0;
        if (newValue < 30) {
          newValue = 30;
        } else if (newValue > 365) {
          newValue = 365;
        }

        if (newValue !== settings?.passwordExpirationSpan) {
          handleFormEdit(newValue, 'passwordExpirationSpan');
        }
      }}
      fieldName="passwordExpirationSpan"
      value={settings?.passwordExpirationSpan}
      label="Require users to reset password after how many days (30-365)?"
      type="number"
      numMin={30}
      numMax={365}
      smallInput
    />
  );
};

const SystemSettings = forwardRef<IRemoteSystemSettingsFunctionsProps, ISystemSettingsProps>(
  ({ notifyHasChanges, isPlatform }, ref) => {
    useImperativeHandle(ref, () => ({
      getData,
    }));

    const dispatch = useDispatch();
    useEffect(() => {
      if (isPlatform) {
        dispatch(getPlatformSettings());
      } else {
        dispatch(getTeams());
        dispatch(getSettings());
      }
    }, [dispatch, isPlatform]);

    const { data, teams } = useSelector((state: RootState) => {
      return {
        data: isPlatform ? state.settings.platformData : state.settings.data,
        teams: state.teams.list,
      };
    });

    const query = useQuery();
    const hasReloadConfig = query.get('reloadConfigs') === '1';
    const prevSettings = useRef<ISettings | undefined>();
    const [settings, setSettings] = useState<ISettings>(data || ({} as ISettings));
    const [currentNotice, setCurrentNotice] = useState<INotice | undefined>();
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const { maintenance } = settings;
    const maintenanceScheduled = !!maintenance?.scheduled;
    useEffect(() => {
      if (data && !isEqual(prevSettings.current, data)) {
        setSettings(data);
        if (notifyHasChanges) {
          notifyHasChanges(false);
        }
      }

      prevSettings.current = cloneDeep(data);
    }, [data]);

    const handleFormEdit = (value: string | number | string[], prop?: string) => {
      const updatedSettings = cloneDeep(settings);
      if (prop) {
        // Need to do this for the passwordExpirationSpan field
        // otherwise it gets set as a string and isEqual won't match
        // if the value is changed back to the original
        updatedSettings[prop] =
          prop === 'passwordExpirationSpan' ? parseInt(value as string, 10) : value;
        setSettings(updatedSettings);
      }
    };

    const hasChanges = !isEqual(settings, prevSettings.current);
    notifyHasChanges(hasChanges);

    // Used by parent component to get form data
    const getData = () => {
      return {
        hasChanges: hasChanges,
        formData: settings,
      };
    };

    const addUpdateNotice = (value: string) => {
      const updatedSettings = cloneDeep(settings);
      if (currentNotice && updatedSettings.notices) {
        const index = updatedSettings.notices.findIndex((n) => n._id === currentNotice._id);
        if (index > -1) {
          updatedSettings.notices[index].value = value;
        }
      } else {
        if (!updatedSettings.notices) {
          updatedSettings.notices = [];
        }

        updatedSettings.notices.unshift({ _id: new ObjectID().toHexString(), value });
      }

      setSettings(updatedSettings);
    };

    const onAddNoticeClick = () => {
      setCurrentNotice(undefined);
      setShowNoticeModal(true);
    };

    const onEditNoticeClick = (notice: INotice) => {
      setCurrentNotice(notice);
      setShowNoticeModal(true);
    };

    const onDeleteNoticeClick = (notice: INotice) => {
      const updatedSettings = cloneDeep(settings);
      if (updatedSettings.notices) {
        const index = updatedSettings.notices.findIndex((n) => n._id === notice._id);
        if (index > -1) {
          updatedSettings.notices.splice(index, 1);
          setSettings(updatedSettings);
        }
      }
    };

    const onReloadConfigsClick = () => {
      dispatch(reloadSSOConfigs());
    };

    const moveNotice = (dragId: string, hoverId: string, dragUp: boolean) => {
      if (dragId && hoverId) {
        const updatedSettings = cloneDeep(settings);
        if (updatedSettings.notices) {
          const dragIndex = updatedSettings.notices.findIndex((n) => n._id === dragId);
          if (dragIndex > -1) {
            const notice = updatedSettings.notices[dragIndex];
            updatedSettings.notices.splice(dragIndex, 1);
            const hoverIndex = updatedSettings.notices.findIndex((n) => n._id === hoverId);
            if (hoverIndex > -1) {
              if (dragUp) {
                updatedSettings.notices.splice(hoverIndex, 0, notice);
              } else {
                updatedSettings.notices.splice(hoverIndex + 1, 0, notice);
              }

              setSettings(updatedSettings);
            }
          }
        }
      }
    };

    const handleMaintenanceEdit = (field: string, value?: string | boolean) => {
      const updatedMaintenance = cloneDeep(settings.maintenance) || {};
      if (field === 'date' && value) {
        let time = '00:00:00';
        if (updatedMaintenance.start) {
          const existingStart = new Date(updatedMaintenance.start);
          time = `${existingStart.getHours().toString().padStart(2, '0')}:${existingStart
            .getMinutes()
            .toString()
            .padStart(2, '0')}`;
        }

        const dateTime = new Date(`${value}T${time}`);
        updatedMaintenance.start = dateTime;
      } else if (field === 'time' && value) {
        let date = getISODateString();
        if (updatedMaintenance.start) {
          date = getISODateString(new Date(updatedMaintenance.start));
        }

        const dateTime = new Date(`${date}T${value}`);
        updatedMaintenance.start = dateTime;
      } else {
        (updatedMaintenance as any)[field] = value;
      }

      setSettings({ ...cloneDeep(settings), maintenance: updatedMaintenance });
    };

    const handleMaintenanceButton = () => {
      const updatedSettings = cloneDeep(settings);

      if (maintenanceScheduled) {
        updatedSettings.maintenance = undefined;
      } else if (updatedSettings.maintenance) {
        updatedSettings.maintenance.scheduled = true;
      }

      setSettings(updatedSettings);
    };

    let start: Date | undefined;
    let date: string | undefined;
    let time: string | undefined;
    if (maintenance?.start) {
      date = getISODateString(maintenance.start);
      start = new Date(maintenance.start);
      time = `${start.getHours().toString().padStart(2, '0')}:${start
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }

    const treeData = buildTeamHierarchy({ teams, sorted: true });
    const hasNotices = (settings?.notices?.length ?? 0) > 0 || maintenanceScheduled;
    return (
      <div className="SystemSettings">
        <Form
          formId="settings-form"
          onSubmit={(e: any) => {
            if (hasChanges) {
              dispatch(updateSettings(settings, false));
            }
            e.preventDefault();
          }}
        >
          <FormSection
            sectionName={`${isPlatform ? 'Platform' : 'System'} Notices`}
            sectionDescription="Notices that will display on users' dashboard page."
            sectionAddition={
              <Button onClick={onAddNoticeClick} className="secondary">
                Add Notice
              </Button>
            }
          >
            {hasNotices && (
              <RBForm.Group as={Row} className="Settings-notifications">
                <Col>
                  <div className="notification-header">
                    <Col xs={10} className="notification">
                      Description
                    </Col>
                    <Col xs={2} className="d-flex justify-content-center"></Col>
                  </div>
                  {maintenanceScheduled && !!maintenance?.start && (
                    <div className="notification-row">
                      <Col xs={10} className="notification">
                        {getMaintenanceMessage(maintenance)}
                      </Col>
                      <Col xs={2} className="actions">
                        <span
                          className={classNames('material-icons')}
                          onClick={handleMaintenanceButton}
                        >
                          delete
                        </span>
                      </Col>
                    </div>
                  )}
                  {settings?.notices?.map((n, i) => (
                    <DragDropItem key={n._id} id={n._id as string} index={i} moveItem={moveNotice}>
                      <div className="notification-row">
                        <Col xs={10} className="notification">
                          {n.value}
                        </Col>
                        <Col xs={2} className="actions">
                          <span
                            className={classNames('material-icons')}
                            onClick={() => onEditNoticeClick(n)}
                          >
                            edit
                          </span>
                          <span
                            className={classNames('material-icons')}
                            onClick={() => onDeleteNoticeClick(n)}
                          >
                            delete
                          </span>
                        </Col>
                      </div>
                    </DragDropItem>
                  ))}
                </Col>
              </RBForm.Group>
            )}
          </FormSection>
          {!isPlatform && (
            <>
              <FormSection sectionName="Scheduled Maintenance">
                <div className="maintenance-row">
                  <div className="maintenance-inputs">
                    <FormInput
                      label="Maintenance Date"
                      type="date"
                      value={date}
                      onChange={(e: any) => handleMaintenanceEdit('date', e.target.value)}
                    />
                    <FormInput
                      label={`Start Time (${Timezone})`}
                      type="time"
                      value={time}
                      onChange={(e: any) => handleMaintenanceEdit('time', e.target.value)}
                    />
                    <FormInput
                      label="Duration (Hours)"
                      type="number"
                      value={maintenance?.duration}
                      onChange={(e: any) => handleMaintenanceEdit('duration', e.target.value)}
                    />
                  </div>
                  <Button
                    disabled={
                      !maintenanceScheduled && (!maintenance?.start || !maintenance?.duration)
                    }
                    className="secondary"
                    onClick={handleMaintenanceButton}
                  >
                    {maintenanceScheduled ? 'CANCEL' : 'SCHEDULE'} MAINTENANCE
                  </Button>
                </div>
              </FormSection>
              <FormSection sectionName="Security Settings">
                <TenantSpecificFields handleFormEdit={handleFormEdit} settings={settings} />
              </FormSection>
              <FormSection sectionName="Marmon Reporting Teams">
                <div className="Tree-container">
                  <Tree
                    id="reporting"
                    binarySelect
                    onSelectedChange={(selectedIds: string[]) =>
                      handleFormEdit(selectedIds, 'marmonTeams')
                    }
                    data={treeData}
                    initialSelections={settings.marmonTeams || []}
                    searchable
                    hasClearAll={false}
                  />
                </div>
              </FormSection>
              {hasReloadConfig && (
                <FormSection
                  sectionName="Reload Single Sign-On Configs"
                  sectionAddition={
                    <Button onClick={onReloadConfigsClick} className="secondary">
                      Reload
                    </Button>
                  }
                />
              )}
            </>
          )}
        </Form>
        <AddUpdateNoticeModal
          show={showNoticeModal}
          notice={currentNotice?.value}
          onSave={(notice: string) => {
            addUpdateNotice(notice);
            setCurrentNotice(undefined);
            setShowNoticeModal(false);
          }}
          onHide={() => {
            setCurrentNotice(undefined);
            setShowNoticeModal(false);
          }}
        />
      </div>
    );
  },
);
export default SystemSettings;
